import { FC, FormEvent, useState } from 'react';
import { useRouter } from 'next/router';
import { holdEmail } from '../VoterGuide2026/utils/signup';

const VOTER_GUIDE_PATH = '/election/voting-guide';

const BALLOT_CATEGORIES = [
  'Governor & Lt. Governor',
  'U.S. House',
  'County & city offices',
  'Judges',
  'U.S. Senate',
  'State Legislature',
  'School board',
  'Ballot questions',
];

/**
 * Promo banner for the full address-driven ballot lookup at
 * VOTER_GUIDE_PATH. Submitting hands the address off via a `street` query
 * param, which VoterGuide2026's boot effect picks up the same way it already
 * handles a shared-ballot link.
 *
 * The email is optional. There's no ballot yet to email a link to, so it is
 * held in memory and signed up by VoterGuide2026 once the ballot is built —
 * the same signup as the summary's form. See utils/signup.ts.
 */
const VoterGuideBanner: FC = () => {
  const router = useRouter();
  const [street, setStreet] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;
    const trimmed = street.trim();
    if (!trimmed) return;

    setIsSubmitting(true);
    // Always replace, so an email from an earlier, abandoned submit can't
    // ride along with this one.
    holdEmail(email.trim() || null);
    const hasAppParam =
      router.asPath.includes('?app') || router.asPath.includes('&app');
    const params = new URLSearchParams({ street: trimmed });
    try {
      await router.push(
        `${VOTER_GUIDE_PATH}?${params.toString()}${hasAppParam ? '&app' : ''}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="voter-guide-banner"
      aria-labelledby="voter-guide-banner-heading"
    >
      <div className="voter-guide-banner-container">
        <div className="voter-guide-banner-stripes" aria-hidden="true">
          <span className="voter-guide-banner-stripe voter-guide-banner-stripe-gold" />
          <span className="voter-guide-banner-stripe voter-guide-banner-stripe-teal" />
        </div>
        <div className="voter-guide-banner-inner">
          <div className="voter-guide-banner-text">
            <span className="voter-guide-banner-eyebrow">
              Your Vote MN 2026
            </span>
            <h2
              id="voter-guide-banner-heading"
              className="voter-guide-banner-heading"
            >
              Voter Guide
            </h2>
            <p className="voter-guide-banner-copy">
              Enter your address to see every race, candidate and question on
              your ballot — plus where and when to vote.
            </p>
            <form onSubmit={handleSubmit} className="voter-guide-banner-form">
              <label
                className="voter-guide-banner-label-sr"
                htmlFor="voter-guide-banner-street"
              >
                Street address, city and ZIP
              </label>
              <input
                id="voter-guide-banner-street"
                className="voter-guide-banner-input"
                type="text"
                name="street"
                value={street}
                autoComplete="street-address"
                placeholder="Street address, city and ZIP"
                onChange={(event) => setStreet(event.target.value)}
                disabled={isSubmitting}
                required
              />
              <label
                className="voter-guide-banner-label-sr"
                htmlFor="voter-guide-banner-email"
              >
                Email address (optional)
              </label>
              <input
                id="voter-guide-banner-email"
                className="voter-guide-banner-input voter-guide-banner-input-email"
                type="email"
                name="email"
                value={email}
                autoComplete="email"
                placeholder="Email (optional)"
                aria-describedby="voter-guide-banner-fineprint"
                onChange={(event) => setEmail(event.target.value)}
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className="btn no-text-transform voter-guide-banner-btn"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Loading…' : 'View My Ballot'}
              </button>
            </form>
            <p className="voter-guide-banner-note">
              Your address is used only to find your ballot.
            </p>
            <p
              id="voter-guide-banner-fineprint"
              className="voter-guide-banner-note"
            >
              Add your email and we’ll send you a link to your ballot. By
              signing up, you’ll also receive key election updates in Minnesota
              Today Weekend Edition. You can unsubscribe anytime.
            </p>
          </div>
          <div className="voter-guide-banner-card">
            <h3 className="voter-guide-banner-card-hdg">On your 2026 ballot</h3>
            <ul className="voter-guide-banner-card-list">
              {BALLOT_CATEGORIES.map((category) => (
                <li key={category}>{category}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VoterGuideBanner;
